import React, { useState, useEffect, useCallback } from 'react';

interface HeaderProps {
  /** Whether to show the Manage Abbreviations admin button (MXO-L6 only). */
  canManageAbbreviations?: boolean;
  /** Opens the abbreviations admin panel. */
  onManageAbbreviations?: () => void;
}

/**
 * Application header displaying the AETHER title, theme toggle, a live UTC
 * clock, and (for MXO-L6 members) the Manage Abbreviations button.
 */
const Header: React.FC<HeaderProps> = ({ canManageAbbreviations, onManageAbbreviations }) => {
  const [utcTime, setUtcTime] = useState<string>(getUtcTimeString());
  const [isDarkMode, setIsDarkMode] = useState<boolean>(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setUtcTime(getUtcTimeString());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleThemeToggle = useCallback((): void => {
    setIsDarkMode((prev) => {
      const newMode = !prev;
      document.documentElement.setAttribute('data-theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  }, []);

  return (
    <header className="app-header">
      <h1>
        <span>A.E.T.H.E.R</span> - Aircraft Event Tracking Hub for Enhanced Reliability
      </h1>
      <div className="header-right">
        {canManageAbbreviations && onManageAbbreviations && (
          <button type="button" className="header-admin-btn" onClick={onManageAbbreviations}>
            Manage Abbreviations
          </button>
        )}
        <label className="theme-toggle" aria-label="Toggle light/dark mode">
          <input
            type="checkbox"
            checked={!isDarkMode}
            onChange={handleThemeToggle}
            aria-label="Switch to light mode"
          />
          <span className="toggle-slider">
            <span className="toggle-icon">{isDarkMode ? '🌙' : '☀️'}</span>
          </span>
        </label>
        <div className="header-clock">
          <span className="clock-label">UTC</span>
          {utcTime}
        </div>
      </div>
    </header>
  );
};

/**
 * Returns the current UTC time formatted as HH:MM:SS.
 */
function getUtcTimeString(): string {
  const now = new Date();
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

export default Header;
